import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, Copy, Check, Mail } from "lucide-react";
import { Footer } from "../components/Footer";

// ---------------------------------------------------------------------------
// /press — Self-serve press kit for journalists, newsletter curators
// (Sidebar, Product Hunt, Refind, Kill the Newsletter, Import AI) and
// podcast bookers. English-only by design: international press coverage
// is written in English; local-language coverage is handled 1:1 by email.
// ---------------------------------------------------------------------------

const BRAND = {
  name: "MiProse",
  altName: "情抒",
  tagline: "A letter to your future self. Delivered at midnight, 90 / 180 / 365 days later.",
  launched: "July 2026",
  categories: ["Slow tech", "Wellness", "Anti-AI-fatigue", "Journaling", "Time capsule"],
  founderPitch:
    "MiProse (情抒) is a quiet counter-weight to the AI productivity boom. You write a single, honest letter to your future self, seal it, and choose to forget it for 90, 180, or 365 days. At midnight of the appointed day, an old-friend voice — not a chatbot — delivers your letter back with a short, literary reply. No summaries. No coaching. No streaks. Just the romance of time.",
  contactEmail: "postman@miprose.com",
  siteUrl: "https://miprose.com",
};

const ASSETS = [
  {
    label: "Hero — Desktop (English)",
    href: "/press-kit/hero-desktop.jpg",
    hint: "1600 × 1000 · JPG · 271 KB",
    thumb: "/press-kit/hero-desktop.jpg",
    fit: "cover",
  },
  {
    label: "Hero — Desktop (繁體中文)",
    href: "/press-kit/hero-zh.jpg",
    hint: "1600 × 1000 · JPG · 290 KB",
    thumb: "/press-kit/hero-zh.jpg",
    fit: "cover",
  },
  {
    label: "Hero — Mobile",
    href: "/press-kit/hero-mobile.jpg",
    hint: "428 × 900 · JPG · 240 KB",
    thumb: "/press-kit/hero-mobile.jpg",
    fit: "cover",
  },
  {
    label: "Wax-Seal Logo (1024 px master)",
    href: "/press-kit/wax-seal-1024.png",
    hint: "PNG · transparent · 1.4 MB",
    thumb: "/press-kit/wax-seal-1024.png",
    fit: "contain",
  },
  {
    label: "Wax-Seal Logo (512 px)",
    href: "/press-kit/logo-512.png",
    hint: "PNG · transparent · 400 KB",
    thumb: "/press-kit/wax-seal-1024.png",
    fit: "contain",
  },
  {
    label: "Wax-Seal Logo (192 px)",
    href: "/press-kit/logo-192.png",
    hint: "PNG · social avatar · 61 KB",
    thumb: "/press-kit/wax-seal-1024.png",
    fit: "contain",
  },
  {
    label: "Wax-Seal Favicon (32 px)",
    href: "/press-kit/logo-wax-seal.png",
    hint: "PNG · favicon · 3 KB",
    thumb: "/press-kit/wax-seal-1024.png",
    fit: "contain",
  },
];

const FACT_ROWS = [
  ["Product name", `${BRAND.name} · ${BRAND.altName}`],
  ["One-liner", BRAND.tagline],
  ["Launched", BRAND.launched],
  ["Categories", BRAND.categories.join(" · ")],
  ["Website", BRAND.siteUrl],
  ["Press contact", BRAND.contactEmail],
  ["Languages", "English · 繁體中文 (港) · 繁體中文 (台) · 简体中文"],
  ["Pricing", "USD 19 / 22 / 25  ·  HKD 148 / 168 / 199  ·  TWD 590 / 690 / 790  ·  CNY 128 / 148 / 168"],
];

function CopyButton({ text, testId }) {
  const [ok, setOk] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setOk(true);
      setTimeout(() => setOk(false), 1600);
    } catch {
      /* clipboard unavailable — silent no-op */
    }
  };
  return (
    <button
      type="button"
      onClick={copy}
      data-testid={testId}
      className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase text-[var(--ink-mute)] hover:text-[#8A3A33] transition-colors border-b border-transparent hover:border-[#8A3A33] pb-0.5"
      aria-label="Copy to clipboard"
    >
      {ok ? <Check size={12} /> : <Copy size={12} />}
      {ok ? "Copied" : "Copy"}
    </button>
  );
}

export default function PressPage() {
  useEffect(() => {
    document.title = "Press Kit — MiProse (情抒)";
  }, []);

  return (
    <div data-testid="press-page" className="min-h-screen bg-[var(--bg-paper)] text-[#1c1a17]">
      {/* Top nav strip */}
      <header className="border-b border-[var(--bg-paper-edge)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            data-testid="press-back-home"
            className="inline-flex items-center gap-2 text-sm tracking-[0.22em] uppercase text-[var(--ink-mute)] hover:text-[#8A3A33] transition-colors"
          >
            <ArrowLeft size={14} />
            {BRAND.name}
          </Link>
          <span className="text-xs tracking-[0.32em] uppercase text-[var(--ink-mute)]">Press Kit</span>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24 grid md:grid-cols-[1fr_auto] gap-12 items-start">
        <div>
          <p className="text-xs tracking-[0.36em] uppercase text-[var(--ink-mute)] mb-6">
            For journalists · newsletter curators · podcast bookers
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            The letter you<br />
            <span className="italic text-[#8A3A33]">forgot you wrote.</span>
          </h1>
          <p className="mt-8 text-base md:text-lg leading-[1.85] max-w-2xl text-[#3a352d]">
            {BRAND.founderPitch}
          </p>
          <p className="mt-6 text-xs tracking-[0.22em] uppercase text-[var(--ink-mute)]">
            Word count: 60 · Ready to lift verbatim
            <span className="mx-3 opacity-40">·</span>
            <CopyButton text={BRAND.founderPitch} testId="press-copy-pitch" />
          </p>
        </div>

        <div className="hidden md:flex flex-col items-center gap-3 pt-2" data-testid="press-hero-mark">
          {/* Raw transparent wax-seal PNG — no white circular plate, no
              app-button rounded background. The organic melted-wax edges
              read directly against the warm parchment page for a true
              wabi-sabi physical-artifact feel. */}
          <img
            src="/press-kit/wax-seal-1024.png"
            alt="MiProse wax seal"
            width="152"
            height="152"
            draggable={false}
            className="object-contain select-none pointer-events-none"
            style={{
              width: 152,
              height: 152,
              imageRendering: "-webkit-optimize-contrast",
              filter:
                "drop-shadow(0 6px 14px rgba(60,20,15,0.24)) drop-shadow(0 22px 42px rgba(60,20,15,0.16))",
              transform: "rotate(-3deg)",
            }}
          />
          <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-mute)] mt-1">the mark</span>
        </div>
      </section>

      {/* Fact sheet */}
      <section className="border-t border-[var(--bg-paper-edge)] bg-[var(--bg-paper-deep)]/40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-baseline justify-between mb-10">
            <h2 className="font-display text-2xl md:text-3xl">Fact sheet</h2>
            <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-mute)]">— 01 —</span>
          </div>
          <dl className="grid gap-x-14 gap-y-6 md:grid-cols-[220px_1fr]">
            {FACT_ROWS.map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-xs tracking-[0.24em] uppercase text-[var(--ink-mute)] md:pt-1">{k}</dt>
                <dd className="text-[15px] md:text-base leading-[1.75] text-[#1c1a17] border-b border-dashed border-[var(--bg-paper-edge)] pb-5 md:pb-4">
                  {v}
                  {k === "Press contact" && (
                    <span className="ml-3 inline-block align-middle">
                      <CopyButton text={BRAND.contactEmail} testId="press-copy-email" />
                    </span>
                  )}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      </section>

      {/* Assets */}
      <section className="border-t border-[var(--bg-paper-edge)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="flex items-baseline justify-between mb-10">
            <h2 className="font-display text-2xl md:text-3xl">Downloadable assets</h2>
            <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-mute)]">— 02 —</span>
          </div>

          <a
            href="/press-kit/miprose-press-kit.zip"
            download
            data-testid="press-download-zip"
            className="inline-flex items-center gap-3 bg-[#1c1a17] text-[var(--bg-paper)] px-6 py-3.5 text-xs tracking-[0.28em] uppercase hover:bg-[#8A3A33] transition-colors mb-14 shadow-[0_10px_30px_-15px_rgba(28,26,23,0.6)]"
          >
            <Download size={14} />
            Everything — one zip (2.6 MB)
          </a>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {ASSETS.map((a) => (
              <a
                key={a.href}
                href={a.href}
                download
                data-testid={`press-asset-${a.href.split("/").pop().split(".")[0]}`}
                className="group block"
              >
                <div className={`aspect-[4/3] overflow-hidden bg-[var(--bg-paper-deep)] border border-[var(--bg-paper-edge)] mb-3 group-hover:border-[#8A3A33] transition-colors ${a.fit === "contain" ? "flex items-center justify-center p-6" : ""}`}>
                  <img
                    src={a.thumb}
                    alt={a.label}
                    loading="lazy"
                    style={{ imageRendering: "-webkit-optimize-contrast" }}
                    className={`${a.fit === "contain" ? "max-w-[70%] max-h-[85%] object-contain" : "w-full h-full object-cover"} group-hover:scale-[1.02] transition-transform duration-500`}
                  />
                </div>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[14px] leading-tight text-[#1c1a17] group-hover:text-[#8A3A33] transition-colors">{a.label}</p>
                    <p className="text-[11px] tracking-[0.14em] uppercase text-[var(--ink-mute)] mt-1">{a.hint}</p>
                  </div>
                  <Download size={14} className="mt-1 shrink-0 text-[var(--ink-mute)] group-hover:text-[#8A3A33] transition-colors" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Brand palette */}
      <section className="border-t border-[var(--bg-paper-edge)] bg-[var(--bg-paper-deep)]/40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-baseline justify-between mb-10">
            <h2 className="font-display text-2xl md:text-3xl">Palette &amp; type</h2>
            <span className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-mute)]">— 03 —</span>
          </div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { name: "Parchment", hex: "#F9F6F0", ink: "#1c1a17" },
              { name: "Wax", hex: "#8A3A33", ink: "#F9F6F0" },
              { name: "Ink", hex: "#1c1a17", ink: "#F9F6F0" },
              { name: "Mute", hex: "#8a8579", ink: "#F9F6F0" },
            ].map((c) => (
              <div key={c.hex} className="border border-[var(--bg-paper-edge)]" data-testid={`press-swatch-${c.name.toLowerCase()}`}>
                <div className="aspect-[16/10]" style={{ background: c.hex }} />
                <div className="px-4 py-3 flex items-center justify-between bg-[var(--bg-paper)]">
                  <div>
                    <p className="text-sm">{c.name}</p>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[var(--ink-mute)]">{c.hex}</p>
                  </div>
                  <CopyButton text={c.hex} testId={`press-copy-hex-${c.name.toLowerCase()}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-2">
            <div className="border border-[var(--bg-paper-edge)] bg-[var(--bg-paper)] px-8 py-10">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-mute)] mb-4">Display</p>
              <p className="font-display text-4xl leading-tight">Playfair Display</p>
              <p className="text-[13px] text-[var(--ink-mute)] mt-2">Headlines · romantic serif · italics for verbs of time</p>
            </div>
            <div className="border border-[var(--bg-paper-edge)] bg-[var(--bg-paper)] px-8 py-10">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[var(--ink-mute)] mb-4">Chinese display</p>
              <p className="font-display text-4xl leading-tight" style={{ fontFamily: '"Noto Serif TC","Noto Serif SC","Songti SC",serif' }}>
                Noto Serif · 思源宋體
              </p>
              <p className="text-[13px] text-[var(--ink-mute)] mt-2">繁體 (港·台) · 简体 (陸)</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact / booking */}
      <section className="border-t border-[var(--bg-paper-edge)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-28 flex flex-col md:flex-row items-start md:items-end justify-between gap-10">
          <div className="max-w-xl">
            <p className="text-xs tracking-[0.36em] uppercase text-[var(--ink-mute)] mb-5">Interviews · quotes · review copies</p>
            <h2 className="font-display text-3xl md:text-4xl leading-[1.15]">
              One inbox.<br />
              <span className="italic text-[#8A3A33]">Replies within 48h.</span>
            </h2>
          </div>
          <a
            href={`mailto:${BRAND.contactEmail}?subject=Press%20inquiry%20—%20MiProse`}
            data-testid="press-mailto"
            className="inline-flex items-center gap-3 bg-[#1c1a17] text-[var(--bg-paper)] px-8 py-4 text-xs tracking-[0.28em] uppercase hover:bg-[#8A3A33] transition-colors shadow-[0_10px_30px_-15px_rgba(28,26,23,0.6)]"
          >
            <Mail size={14} />
            {BRAND.contactEmail}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
